/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { describeUnknownPath, expressionStructureIssues, rootCalculatedPath } from './expression-structure';
import {
  checkDestinationReference,
  checkDestinationValue,
  driverHeaderField,
  isRequiredHere,
  readStoredImportance,
  type FieldImportance,
} from '@destination-field';
import { summarizeMacroExpression, type ExpressionNameLookup } from './expression-summary';
import { checkYieldAgainstDestination, rowYield } from '@expression-yield';
import type { MacroExpression } from '../../../types/decorator-expression';
import { canonicalizeContextPath } from '@context-roots';
import type { MappingLeaf } from './ortb-body-editor.adapters';
import type { MappingIssue } from './mapping-issues';
import {
  foldMappingExpression,
  isMacroToken,
  isStructuredHeaderValue,
  readMappingValueSlots,
} from '@mapping-value-slots';
import { originOf, readValueOrigin, withRowDefault, type ValueOrigin } from './parameter-editor.adapters';
import {
  describeArtifactMetadataOnWire,
  describeRequiredClaim,
  describeRowSlots,
  leafFromRowSlots,
  rowSlotsFromLeaf,
  slotsDefault,
  type RequiredMark,
  type RowMappingKind,
  type RowValueCapabilities,
} from './mapping-value-round-trip';
import { formatStaticValue } from './ortb-static-value';
import { isResolvableMacro } from './resolvable-macros';
import { isKnownContextPath, suggestedMacroFor } from '../inputs/data-model-schema';

export interface HeaderRow {
  id: string;
  key: string;
  /** The authored literal. Empty on a row that resolves from somewhere (`N11f`). */
  value: string;
  /** What a header with no expression sends — kept on the row only then (`X58`). */
  default?: string;
  /**
   * **Everything that is not a literal** (`X58`, `#1428`): a path, a `{macro}` (a `macro` node), a
   * calculation, a decision — the one form a header stores, carrying its own default.
   */
  expression?: MacroExpression;
  /**
   * **Never send this header** (`P1` slice 3), including one this driver adds automatically.
   *
   * The only authoring act that can remove a header at all. Mapping one to nothing does not do it
   * — that is *omitted rather than sent empty* (#998), and it leaves the **driver header**
   * underneath in place, which is how `X-Forwarded-For` came to go out on every request with no
   * operator path to stop it.
   */
  suppress?: boolean;
  /**
   * **How much the destination wants this header** (`#1354`, slice `4b-ii`) — the same claim a
   * parameter row makes, kept on the row for the same reason: a template can say *required, and only
   * you have it*, and an instance with nothing here is reported as unfilled. It never changes what is
   * sent (`X46`), so the driver does not read it.
   */
  importance?: FieldImportance;
  /**
   * **Whether this row's literal is a credential** (`CR2`, `#1371`): stored encrypted, never read back.
   * `false` is an operator's untick of a suggestion, held for the visit; only `true` is stored.
   */
  secret?: boolean;
  /**
   * How this row came to be (R4/R4b). Absent means the operator set it.
   *
   * Persisted in `DemandSourceDto.headersProvenance`, a side-car keyed by header
   * name — `headers` is a flat map handed to the driver, so metadata written into
   * it would go on the wire.
   */
  provenance?: ValueOrigin;
}

type HeaderValueConfig = {
  suppress?: boolean;
  value?: string;
  macro?: string;
  default?: string;
  /** Pre-`#1216` spelling of `default` (`X3`), read off a stored header. */
  fallback?: string;
  mappingSource?: string;
  expression?: MacroExpression;
  /** How much the destination wants this header (`#1354`) — never read by the driver (`X46`). */
  importance?: FieldImportance;
  /** The literal is a credential (`#1371`). */
  secret?: boolean;
};

/**
 * **What a header row is built from** — a row, or the slots a document written before `X58` stored:
 * a `macro`, a typed `mappingSource`, a row-level `default`. {@link createHeaderRow} reads them into the
 * row's one expression.
 */
export type HeaderRowInput = Partial<HeaderRow> & { macro?: string; mappingSource?: string; default?: string };

export function createHeaderRow(partial?: HeaderRowInput): HeaderRow {
  // **One place the row's value slots are split** (`N11f`) — every header row in the console
  // is built here, and every caller wrote the token into `value` because that is where it
  // lived. Read through the same function the driver reads the stored document with.
  const resolvesFromSomewhere =
    !!partial?.macro || !!partial?.mappingSource || !!partial?.expression || isMacroToken(partial?.value);
  const slots = readMappingValueSlots({
    value: partial?.value,
    macro: partial?.macro,
    default: partial?.default,
    mappingSource: partial?.mappingSource,
    expression: partial?.expression,
    type: resolvesFromSomewhere ? undefined : 'static',
  });

  // **The header store's legacy read** (`X58`, `#1428`): a macro or a typed path is the expression it
  // is, canonicalised where it enters the row (`#1213`). A row default beside an expression that
  // declares none of its own moves onto it.
  const folded = foldMappingExpression({
    macro: slots.macro,
    mappingSource: canonicalizeContextPath(partial?.mappingSource ?? ''),
    expression: partial?.expression,
    default: slots.default,
  }) as MacroExpression | undefined;
  const expression = withRowDefault(folded, partial?.expression ? slots.default : undefined);

  return {
    id: partial?.id ?? `hdr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    key: partial?.key ?? '',
    value: slots.literal !== undefined ? String(slots.literal) : '',
    expression,
    ...(expression ? {} : { default: slots.default }),
    /**
     * **Carried, or *Never send* does not survive a load** (found 2026-09-23 in `4b-ii`). This builder
     * was rewritten for `N11f` the day before header suppression shipped (`4e8b7344`), which passed
     * `suppress` in and had it dropped here — so a stored `{ suppress: true }` loaded as a live row and
     * saved back as `""`, and the driver's `X-Forwarded-For` went out again. The spec for the feature
     * tested the driver; only the round trip through this function could see it.
     */
    ...(partial?.suppress ? { suppress: true } : {}),
    ...(partial?.importance ? { importance: partial.importance } : {}),
    // Carried for the reason `suppress` is: a flag dropped here does not survive a load (`#1371`).
    ...(partial?.secret !== undefined ? { secret: partial.secret } : {}),
    provenance: partial?.provenance,
  };
}

/**
 * @param provenance `DemandSourceDto.headersProvenance` — the side-car keyed by
 *   header name. A key no longer present in `headers` simply never gets read, which
 *   is the staleness rule: an entry that cannot be resolved is absent, not an error.
 */
export function headersObjectToRows(
  headers?: Record<string, unknown>,
  provenance?: Record<string, unknown>,
): HeaderRow[] {
  if (!headers || typeof headers !== 'object') return [];
  // The pre-`#1001` bare word is still stored until the source is next saved (`readValueOrigin`).
  const provenanceFor = (key: string): ValueOrigin | undefined => readValueOrigin(provenance?.[key]);

  return Object.entries(headers).map(([key, rawValue]) => {
    if (isStructuredHeaderValue(rawValue)) {
      const stored = rawValue as HeaderValueConfig;
      const expression = stored.expression;
      return createHeaderRow({
        key,
        value: formatStaticValue(stored.value),
        macro: stored.macro,
        // The stored header's own legacy read: `readMappingValueSlots` sees only what this
        // hands it, so the pre-`#1216` spelling is translated at the boundary (`X3`).
        default: stored.default ?? stored.fallback,
        mappingSource: stored.mappingSource ?? (expression?.type === 'calculated' ? expression.path : ''),
        expression,
        suppress: stored.suppress,
        importance: readStoredImportance(stored),
        ...(stored.secret === true ? { secret: true } : {}),
        provenance: provenanceFor(key),
      });
    }

    return createHeaderRow({ key, value: formatStaticValue(rawValue), provenance: provenanceFor(key) });
  });
}

/**
 * Build the side-car from rows.
 *
 * Separate from {@link rowsToHeadersObject} on purpose: that function's output is
 * handed to the driver as request headers, so it must stay pure. Provenance written
 * into it would go on the wire.
 */
export function rowsToHeadersProvenance(rows: HeaderRow[]): Record<string, ValueOrigin> {
  const out: Record<string, ValueOrigin> = {};
  rows.forEach((row) => {
    const key = row.key.trim();
    if (key && row.provenance) out[key] = row.provenance;
  });
  return out;
}

export function rowsToHeadersObject(rows: HeaderRow[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  rows.forEach((row) => {
    const k = row.key.trim();
    if (!k) return;

    if (row.expression || row.default || row.suppress || row.importance || row.secret) {
      result[k] = {
        // Written even when nothing else is, because it is the one slot that means something on
        // its own: a row that only says *never send this* has no value to carry.
        ...(row.suppress ? { suppress: true } : {}),
        ...(row.value ? { value: row.value } : {}),
        // **One form** (`X58`): the expression, which carries its own default.
        ...(row.expression ? { expression: row.expression } : {}),
        ...(!row.expression && row.default ? { default: row.default } : {}),
        ...(row.importance ? { importance: row.importance } : {}),
        // A secret row is structured even when its literal is all it says, so the flag is stored.
        ...(row.secret ? { secret: true } : {}),
      };
      return;
    }

    result[k] = row.value;
  });
  return result;
}

// ---------------------------------------------------------------------------
// Paradigm rebuild (#851): header value ↔ MappingLeaf, a standard-header catalog,
// and validation into the shared warnings model. Mirrors the parameter editor —
// a header is a `key → value-mapping` row, where the value is exactly the
// static / macro / path / expression the shared MappingControl handles.
// ---------------------------------------------------------------------------

export interface HeaderCatalogField {
  key: string;
  value: string;
  mappingSource: string;
  description: string;
}

/**
 * Common request headers offered by "Add recommended". The proxy already forwards
 * the incoming request's headers, so these are overrides / additions. Mapping
 * paths match the real session shape (`session.user.*`), verified against
 * context-value-resolver.ts.
 */
export const STANDARD_HEADERS: ReadonlyArray<HeaderCatalogField> = [
  { key: 'Accept', value: '*/*', mappingSource: '', description: 'Response content types accepted' },
  { key: 'User-Agent', value: '{ua}', mappingSource: 'session.user.userAgent', description: 'Device user-agent' },
  {
    key: 'X-Forwarded-For',
    value: '{ip}',
    mappingSource: 'session.user.ipAddress',
    description: 'Originating client IP',
  },
  {
    key: 'X-Device-Type',
    value: '{device_type}',
    mappingSource: 'session.user.deviceType',
    description: 'Device type',
  },
];

/**
 * Header names offered as you type (#922-class: assist the moment of the action).
 *
 * Wider than {@link STANDARD_HEADERS}, which is the *recommended* set the "add missing"
 * button seeds. This is the *known* set — what an operator might legitimately set on a
 * demand-source call — so typing "x-f" reaches `X-Forwarded-For` without having to
 * remember the exact casing or hyphenation, which is where header names are easy to get
 * subtly wrong.
 *
 * Deliberately excludes the three the drivers own (`User-Agent`, `Accept`,
 * `Content-Type`): suggesting them invites an operator to author a row that duplicates a
 * driver header and outranks the session's copy of the real thing — the defect the
 * presets used to have.
 */
export interface HeaderNameSuggestion {
  name: string;
  description: string;
  /** Common values, offered once the name is known. */
  values?: string[];
}

export const HEADER_NAME_CATALOG: ReadonlyArray<HeaderNameSuggestion> = [
  {
    name: 'X-Forwarded-For',
    description: 'Originating client IP — several exchanges geo-locate on it',
    values: ['{ip}'],
  },
  { name: 'X-Device-Type', description: 'Device type (ctv, mobile, desktop)', values: ['{device_type}'] },
  { name: 'X-Real-IP', description: 'Client IP, alternative to X-Forwarded-For', values: ['{ip}'] },
  { name: 'Accept-Language', description: 'Content language preference', values: ['{language}', 'en-US,en;q=0.9'] },
  { name: 'Accept-Encoding', description: 'Response encodings accepted', values: ['gzip, deflate'] },
  { name: 'Authorization', description: 'Auth credential required by some exchanges', values: ['Bearer '] },
  { name: 'X-API-Key', description: 'API key required by some exchanges' },
  {
    name: 'X-OpenRTB-Version',
    description: 'OpenRTB version, when the exchange requires it explicitly',
    values: ['2.5', '2.6'],
  },
  { name: 'Referer', description: 'Page or app referrer', values: ['{page_url}'] },
  { name: 'Origin', description: 'Request origin' },
  { name: 'X-Request-Id', description: 'Correlation id for the exchange support team' },
  { name: 'Cookie', description: 'Cookie header, where an exchange syncs on it' },
];

/** Header-name suggestions matching a partial input, best-prefix first. */
export function suggestHeaderNames(query: string): HeaderNameSuggestion[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...HEADER_NAME_CATALOG];
  const starts = HEADER_NAME_CATALOG.filter((h) => h.name.toLowerCase().startsWith(q));
  const contains = HEADER_NAME_CATALOG.filter(
    (h) => !h.name.toLowerCase().startsWith(q) && h.name.toLowerCase().includes(q),
  );
  return [...starts, ...contains];
}

/** Common values for a header name, for the value side of the row. */
export function suggestHeaderValues(name: string): string[] {
  const match = HEADER_NAME_CATALOG.find((h) => h.name.toLowerCase() === name.trim().toLowerCase());
  return match?.values ? [...match.values] : [];
}

export function getMissingStandardHeaders(existingKeys: ReadonlySet<string>): ReadonlyArray<HeaderCatalogField> {
  const have = new Set(Array.from(existingKeys, (k) => k.trim().toLowerCase()));
  return STANDARD_HEADERS.filter((h) => !have.has(h.key.toLowerCase()));
}

/**
 * `template` (R4/R4b): the catalog composed this header's value and mapping for the
 * protocol, not for this source. The click authored the inclusion; provenance
 * describes the value.
 */
export function headerRowFromCatalog(field: HeaderCatalogField): HeaderRow {
  return createHeaderRow({
    key: field.key,
    value: field.value,
    mappingSource: field.mappingSource,
    provenance: originOf('template'),
  });
}

/**
 * The row a header name should produce when it is added by hand (#922 · 2).
 *
 * The parameter surface's `rowForParamName` with the header catalog — same rule, same
 * order, deliberately not a second policy. Guidance §5 ("pre-map added fields, never
 * leave a blank") held for `+ Add recommended` on both surfaces and for neither
 * add-by-name, which is what made it read as a property of that one button.
 *
 * Catalog first, then {@link matchMacroName} (whole-token only, #1003), then a static
 * row. A header name we recognise neither way is genuinely one we have no opinion
 * about — `X-Partner-Token` should not be guessed at.
 */
export function headerRowForKey(key: string): HeaderRow {
  const trimmed = key.trim();
  const lower = trimmed.toLowerCase();
  const catalogEntry = STANDARD_HEADERS.find((h) => h.key.toLowerCase() === lower);
  if (catalogEntry) return headerRowFromCatalog(catalogEntry);

  // Canonical name, as in `rowForParamName` — seeding is an offer.
  const macro = suggestedMacroFor(trimmed);
  if (macro) {
    return createHeaderRow({
      key: trimmed,
      value: `{${macro.macro}}`,
      mappingSource: macro.path,
      provenance: originOf('template'),
    });
  }

  return createHeaderRow({ key: trimmed });
}

/**
 * A header row has **no description field**, so a plain path carrying one keeps its
 * expression rather than losing the note. Safe here and not on a parameter row: a header's
 * `mappingSource` is converted to a `calculated` expression before it is resolved, and an
 * empty result is omitted either way (#998) — so the two forms cannot disagree on the wire.
 */
const HEADER_ROW_SLOTS: RowValueCapabilities = { absorbsDescription: false };

/** Project a header row's value into the MappingLeaf the shared control edits. */
export function headerRowToLeaf(row: HeaderRow): MappingLeaf {
  return leafFromRowSlots([row.key], { value: row.value ?? '', expression: row.expression });
}

/** Fold an edited MappingLeaf back into a header row (key preserved). */
export function applyLeafToHeaderRow(row: HeaderRow, leaf: MappingLeaf): HeaderRow {
  const slots = rowSlotsFromLeaf(leaf, HEADER_ROW_SLOTS);
  const expression = withRowDefault(slots.expression, row.default);
  return { ...row, value: slots.value, expression, default: expression ? undefined : row.default };
}

/** One-line summary of a header row's value mapping — for the collapsed chip. */
export function describeHeaderMapping(
  row: HeaderRow,
  /** Names a shared expression the row addresses (`XS5`, `#1066`); see `describeRowMapping`. */
  nameFor?: ExpressionNameLookup,
): { label: string; kind: RowMappingKind } {
  return describeRowSlots(
    { value: row.value ?? '', expression: row.expression },
    /**
     * **This returned the literal word `expression`** until `XS5` — a **third** summariser, and the
     * exact defect `expression-summary.ts` was extracted to end: `ResponseMappingEditor`'s private
     * one returned `s.type`, so a conditional row read `conditional`. A header row holding a
     * calculation said *expression* and nothing about what it does.
     *
     * It was invisible because it is correct in the only case anybody looks at: a header is usually
     * a path or a literal, and neither reaches this branch.
     */
    (expression) => summarizeMacroExpression(expression, nameFor),
  );
}

/**
 * The driver's vocabulary — see `resolvable-macros.ts`. This was a hand-kept subset
 * and drifted the same way the parameter list did: the header expander is the same
 * `expandMacrosInString`, so anything the driver resolves is valid in a header.
 *
 * The "sent literally" consequence below stays, and is correct **here and only
 * here**: an unresolved macro in a header is left as written, because a literal
 * token is inert to the receiver and visible to the operator in the inspector.
 * Parameters and ORTB body leaves deliberately behave differently.
 */

/**
 * **The required mark on a header row** (`#1354`, `4b-ii`) — the parameter row's and the body leaf's
 * projection, with this surface's consequence: a header that resolves to nothing is **omitted**
 * (#998), so it never goes out empty. A suppressed row reports `required: false`, as on the others.
 */
export function describeHeaderRequiredMark(row: HeaderRow, isTemplate: boolean | null): RequiredMark | null {
  return describeRequiredClaim(
    {
      required: isRequiredHere(row.importance) && !row.suppress,
      answered: row.value.trim() !== '' || !!row.expression,
      whenUnresolved: 'omit',
    },
    isTemplate,
  );
}

/** Validate header rows into the shared warnings model (guidance §16). */
export function validateHeaders(
  rows: HeaderRow[],
  /**
   * **Names what a row addresses, so the shape check can read a shared expression's declaration**
   * (`#1458`). Required and nullable for `validateParams`' reason (`#1254`): optional, the callers that
   * forgot would answer with less and say nothing.
   */
  expressionLookup: ExpressionNameLookup | null,
): MappingIssue[] {
  const issues: MappingIssue[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) continue;
    const lower = key.toLowerCase();
    if (seen.has(lower)) {
      issues.push({ kind: 'conflict', message: `Duplicate header "${key}" — the last one wins.`, ref: key });
    }
    seen.add(lower);

    // A path the model does not have resolves to its default on every request —
    // silently, and invisibly when a `{macro}` beside it supplies the value anyway
    // (#998). Detectable here because a `calculated` mapping resolves by literally
    // walking the path.
    // The root's path is this surface's to check; `[*]` there is the expression walk's, below.
    const rootPath = rootCalculatedPath(row.expression);
    if (rootPath && !rootPath.includes('[*]') && !isKnownContextPath(rootPath)) {
      issues.push({ kind: 'unresolved', message: describeUnknownPath(`"${key}"`, rootPath), ref: key });
    }

    // Metadata about the demand source, going on the wire (`#1322`). The sentence is shared.
    for (const message of describeArtifactMetadataOnWire(row, `"${key}"`)) {
      issues.push({ kind: 'metadata-on-wire', message, ref: key });
    }

    // What is unfinished **inside** the row's expression (`4c`, `#1334`).
    issues.push(...expressionStructureIssues(row.expression, `"${key}"`, key, 'row'));

    /**
     * **A driver header declares what it carries, so a row overriding one is checked against it**
     * (`FD4`, slice `4b-ii`). A literal and a default are graded by the concept's grammar — `text`,
     * since a header is text — and what the row points at is checked against the concept (`4d`):
     * `X-Forwarded-For` mapped to the user agent is reported the way a query parameter would be. A
     * header the driver does not write declares nothing, so it is never graded.
     */
    if (!row.suppress) {
      const declared = driverHeaderField(key);
      for (const literal of [
        row.expression ? undefined : row.value || undefined,
        slotsDefault({ expression: row.expression }) ?? row.default,
      ]) {
        const finding = checkDestinationValue(declared, literal, 'text');
        if (finding) issues.push({ kind: finding.kind, message: finding.message, ref: key });
      }
      const reference = checkDestinationReference(declared, {
        macro: row.expression?.type === 'macro' ? `{${row.expression.name}}` : null,
        path: rootCalculatedPath(row.expression),
      });
      if (reference) issues.push({ kind: reference.kind, message: reference.message, ref: key });

      /**
       * **Shape against shape, on a header** (`#1458`, Todd 2026-09-28). Every header carries text,
       * so the slot is text whatever the driver writes. A header is a text surface: a list goes out
       * comma-joined, HTTP's own list form (RFC 9110 §5.6.1), and is not a finding. An object is, and
       * the only way out named is one member of it — *Send structured values as* is refused here (one
       * name, one value), so the redirect must not offer it (`#1272`).
       */
      if (row.expression) {
        const lookup = expressionLookup ? (ref: string) => expressionLookup(ref)?.expression : null;
        const target = row.expression.type === 'reference' ? expressionLookup?.(row.expression.ref) : undefined;
        const shape = checkYieldAgainstDestination(
          { name: key, valueType: 'string' },
          rowYield(undefined, row.expression, lookup),
          {
            from: target?.name ?? null,
            path: rootCalculatedPath(row.expression),
            offersRendering: false,
            rendered: false,
            textSurface: true,
          },
        );
        if (shape) issues.push({ kind: shape.kind, message: shape.message, ref: key });
      }
    }

    const summary = describeHeaderMapping(row);
    if (summary.kind === 'empty') {
      issues.push({ kind: 'missing-value', message: `"${key}" has no value and won't be sent.`, ref: key });
    } else if (summary.kind === 'macro') {
      const token = row.expression?.type === 'macro' ? row.expression.name : null;
      if (token && !isResolvableMacro(token)) {
        issues.push({
          kind: 'unresolved',
          message: `"${key}" uses an unknown macro {${token}} that won't resolve — it will be sent literally.`,
          ref: key,
        });
      }
    }
  }
  return issues;
}
